import { gql } from '@apollo/client';
import { Button, FormControl, FormLabel, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { VolumeHighIcon, VolumeLowIcon, VolumeMuteIcon } from '~components/ui/icons';
import { Slider } from '~components/ui/Slider';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayVolumeSettings_ConfigFragment,
  useConfigUpdateVolumeLevelMutation,
  useConfigUpdateVolumeMuteMutation,
} from './__generated__/ConfigDisplayVolumeSettings.graphql';

interface Props {
  config: ConfigDisplayVolumeSettings_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigDisplayVolumeSettings({ config, configStatus }: Props) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const [volumeLevel, setVolumeLevel] = useState(0);
  const isMuteButtonDisabled = useMemo(() => isNil(config.volume?.isMuted), [config]);
  const isMuted = useMemo(() => Boolean(config.volume?.isMuted?.reported), [config]);
  const { t } = useTranslation();
  const analytics = useAnalyticsReporter();
  const isVolumeSliderDisabled = useMemo(
    () => isNil(config.volume?.level) || isMuted,
    [config, isMuted],
  );
  const volumeLevelMin = useMemo(() => config.volume?.limits?.min?.reported ?? 0, [config]);
  const volumeLevelMax = useMemo(() => config.volume?.limits?.max?.reported ?? 100, [config]);

  const hasConfigUpdatePermission = verifyUserPermissions([Permission.ConfigurationUpdate]);

  useEffect(() => {
    // If a new value comes in from the server, the slider should update.
    // But only if it's an actual value.
    const newVolume = config.volume?.level?.reported;

    if (!isNil(newVolume)) {
      setVolumeLevel(newVolume);
    }
  }, [config]);

  const [updateVolumeLevel, { loading: isUpdateVolumeMutationLoading }] =
    useConfigUpdateVolumeLevelMutation();
  const handleSliderChangeEnd = async (value: number) => {
    try {
      await updateVolumeLevel({
        variables: {
          input: {
            configurationId: config.id,
            level: value,
          },
        },
        // Technically this optimistic response isn't needed.
        // But let's already have Apollo update its cache in case we read the value somewhere else.
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });

      analytics.track('displaySettingUpdate', { group: 'playback', changeItem: 'volume' });
    } catch (err) {
      toast({
        status: 'error',
        title: t('volumeLevelErr'),
        description: fromError(err, 'UpdateVolumeLevel', {
          VOLUME_LEVEL_OUT_OF_RANGE: t('volumeLevelErrDesc'),
        }),
      });
    }
  };

  const [updateVolumeMute, { loading: isMuteMutationLoading }] =
    useConfigUpdateVolumeMuteMutation();
  const handleMuteButtonClick = useCallback(async () => {
    try {
      await updateVolumeMute({
        variables: {
          input: {
            configurationId: config.id,
            mute: !isMuted,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });

      analytics.track('displaySettingUpdate', { group: 'playback', changeItem: 'mute' });
    } catch (err) {
      toast({
        status: 'error',
        title: t('muteStateErr'),
        description: fromError(err, 'UpdateMute'),
      });
    }
  }, [analytics, config.id, isMuted, toast, updateVolumeMute, t]);

  const SpeakerIcon = () => {
    const Icon =
      volumeLevel === 0 || isMuted
        ? VolumeMuteIcon
        : volumeLevel <= 50
        ? VolumeLowIcon
        : VolumeHighIcon;

    return (
      <Button
        variant="unstyled"
        onClick={handleMuteButtonClick}
        isDisabled={
          checkStateToDisableTab(configStatus)
            ? true
            : !hasConfigUpdatePermission || isMuteButtonDisabled
        }
        aria-label={volumeLevel === 0 || isMuted ? 'Unmute' : 'Mute'}
        minWidth="unset"
      >
        <Icon width="6" height="6" />
      </Button>
    );
  };

  const isLoading = isMuteMutationLoading || isUpdateVolumeMutationLoading;

  return (
    <FormControl>
      <FormLabel>
        {t('volume')}
        <FormLabelPendingIndicator isPending={isLoading} />
      </FormLabel>
      <Slider
        value={volumeLevel}
        onChange={setVolumeLevel}
        onChangeEnd={handleSliderChangeEnd}
        leftIcon={SpeakerIcon}
        isDisabled={
          checkStateToDisableTab(configStatus)
            ? true
            : !hasConfigUpdatePermission || isVolumeSliderDisabled
        }
        minScale={0}
        maxScale={100}
        min={volumeLevelMin}
        max={volumeLevelMax}
      />
    </FormControl>
  );
}

ConfigDisplayVolumeSettings.graphql = {
  fragments: {
    ConfigDisplayVolumeSettings_config: gql`
      fragment ConfigDisplayVolumeSettings_config on ConfigurationModel {
        id
        volume {
          isMuted {
            reported
          }
          level {
            reported
          }
          limits {
            min {
              reported
            }
            max {
              reported
            }
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateVolumeLevel: gql`
      mutation ConfigUpdateVolumeLevel($input: ConfigurationUpdateVolumeLevelInput!) {
        configurationUpdateVolumeLevel(input: $input) {
          id
          ...ConfigDisplayVolumeSettings_config
        }
      }
    `,
    ConfigUpdateVolumeMute: gql`
      mutation ConfigUpdateVolumeMute($input: ConfigurationUpdateVolumeMuteInput!) {
        configurationUpdateVolumeMute(input: $input) {
          id
          ...ConfigDisplayVolumeSettings_config
        }
      }
    `,
  },
};
